import { Action } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { isString } from 'utils/type-check';
import { ApiError } from 'types/api-error';
import bridge from 'src/libs/bridge';
import { IAppInfo } from '@byted/ecom-mobile-lib/lib/types/bridge';
export interface ApiResult<R> {
    data?: {
        st?: number;
        code?: number;
        data?: R;
        msg?: string;
    };
}
export type ThunkResult<R, S> = ThunkAction<R, S, undefined, Action>;
export type ActionGen<R, S> = (payload: R) => ThunkResult<any, S>;
type StringOrActionGen<R, S> = string | ActionGen<R, S>;
const wrapper = <R, S>(
    loading: StringOrActionGen<R, S>,
    failure: StringOrActionGen<R, S>,
    success: StringOrActionGen<R, S>,
    apiPromise: Promise<ApiResult<R>>,
    rejectMark?: boolean
): ThunkAction<Promise<R | undefined | ApiError>, S, undefined, Action> => {
    const getAction = (action: StringOrActionGen<R, S>, payload: any): any => {
        return isString(action)
            ? {
                  type: action,
                  payload: payload
              }
            : (action as ActionGen<R, S>)(payload);
    };
    return (dispatch: ThunkDispatch<S, undefined, Action>) => {
        dispatch(getAction(loading, true));
        return apiPromise
            .then((res) => {
                if (res.data && res.data.st === 0) {
                    return res.data.data;
                }
                if (res.data && res.data.code === 0) {
                    return res.data.data;
                }
                // eslint-disable-next-line no-throw-literal
                throw {
                    code: res && res.data && (res.data.st || res.data.code || 10),
                    message: res && res.data && res.data.msg,
                    data: res && res.data && res.data.data
                };
            })
            .then((result) => {
                dispatch(getAction(success, result));
                return result;
            })
            .catch((e: Error | ApiError) => {
                const apiError =
                    e instanceof Error
                        ? {
                              code: 10000,
                              message: '网络错误'
                          }
                        : e;

                dispatch(getAction(failure, apiError));
                let did = '0';
                bridge
                    .call('appInfo', {}, { timeout: 500 })
                    .then((res) => {
                        did = (res as IAppInfo).device_id || '0';
                    })
                    .finally(() => {
                        window.Slardar('emit', 'log', {
                            value: JSON.stringify(apiError),
                            tags: {
                                type: 'api error',
                                action: failure,
                                did
                            }
                        });
                    })
                    .catch(() => {});

                if (rejectMark) return Promise.reject<ApiError>(apiError);
            });
    };
};

export function genApiActions(type: string) {
    return {
        SUCCESS: `${type}_SUCCESS`,
        REQUEST: `${type}_REQUEST`,
        FAILURE: `${type}_FAILURE`
    };
}

export function apiActionWrapper<R, S>(
    action: {
        SUCCESS: StringOrActionGen<R, S>;
        REQUEST: StringOrActionGen<R, S>;
        FAILURE: StringOrActionGen<R, S>;
    },
    apiPromise: Promise<ApiResult<R>>,
    rejectMark: boolean = true
) {
    return wrapper(action.REQUEST, action.FAILURE, action.SUCCESS, apiPromise, rejectMark);
}

export default wrapper;
